import { useState, useEffect } from 'react';
import { Card } from '../../../../components/ui/Card';
import { Button } from '../../../../components/ui/Button';
import { Switch } from '../../../../components/ui/Switch';
import { ConsentModal } from '../../../../components/ui/ConsentModal';
import { Info, Share2, Loader2, AlertTriangle } from 'lucide-react';
import { settingsApi } from '../../../../services/api/endpoints/settings';
import { usePermissions } from '../../../../hooks/usePermissions';
import type { DataSharingOptions, DataSharingSettings as DataSharingSettingsType } from '../../../../types/settings';
import toast from 'react-hot-toast';

const SHARING_LABELS: Record<keyof DataSharingOptions, { label: string; description: string }> = {
  banks:        { label: 'Banques',                              description: "Facilite l'accès aux crédits bancaires et services financiers" },
  microfinance: { label: 'Institutions de Microfinance',         description: "Permet d'accéder à des microcrédits et solutions de financement adaptées" },
  coopec:       { label: 'COOPEC',                               description: 'Accès à des solutions de financement coopératives' },
  analysts:     { label: 'Analystes de i-KiotaHub',              description: 'Bénéficiez de conseils personnalisés basés sur vos données' },
  partners:     { label: 'Partenaires techniques et financiers', description: "Facilite l'accès aux subventions et investissements" },
};

const DEFAULT_OPTIONS: DataSharingOptions = {
  banks: false, microfinance: false, coopec: false, analysts: false, partners: false,
};

export function DataSharingSettings() {
  const { isAdmin, can } = usePermissions();
  const canManageIntegrations = can('manage_integrations') || isAdmin;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [sharingOptions, setSharingOptions] = useState<DataSharingOptions>(DEFAULT_OPTIONS);
  const [enableAll, setEnableAll] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Tracks which option (or 'all') triggered the consent modal
  const [pendingOption, setPendingOption] = useState<keyof DataSharingOptions | 'all' | null>(null);

  /**
   * Garde uniquement les 5 champs de partage. Le backend rejette toute
   * propriété non whitelisted (companyId, lastModified, modifiedBy, etc.)
   * via `forbidNonWhitelisted: true`. On nettoie donc systématiquement
   * la réponse serveur avant de la stocker en state.
   */
  const pickOptions = (data: Partial<DataSharingSettingsType>): DataSharingOptions => ({
    banks: !!data.banks,
    microfinance: !!data.microfinance,
    coopec: !!data.coopec,
    analysts: !!data.analysts,
    partners: !!data.partners,
  });

  // Chargement initial
  useEffect(() => {
    if (!canManageIntegrations) return;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await settingsApi.getDataSharingSettings();
        if (!response.success || !response.data) throw new Error();
        const data = response.data as DataSharingSettingsType;
        const options = pickOptions(data);
        setSharingOptions(options);
        setConsentGiven(!!data.consentGiven);
        setEnableAll(Object.values(options).every(Boolean));
      } catch {
        setError('Impossible de charger les paramètres de partage. Veuillez réessayer.');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [canManageIntegrations]);

  const save = async (
    newOptions: DataSharingOptions,
    newEnableAll: boolean,
    newConsent?: boolean
  ) => {
    if (!canManageIntegrations) return;
    setIsSaving(true);
    setError(null);
    try {
      // On n'envoie QUE les champs typés du DTO backend — pas de companyId,
      // pas de lastModified renvoyés par le serveur (le backend les rejette).
      const cleanedOptions = pickOptions(newOptions);
      const payload = { ...cleanedOptions, consentGiven: newConsent ?? consentGiven };
      const response = await settingsApi.updateDataSharingSettings(payload);
      if (!response.success || !response.data) throw new Error();
      const data = response.data as DataSharingSettingsType;
      const updatedOptions = pickOptions(data);
      setSharingOptions(updatedOptions);
      setEnableAll(newEnableAll);
      if (newConsent !== undefined) setConsentGiven(!!data.consentGiven);
      toast.success('Paramètres de partage enregistrés');
    } catch {
      setError('Impossible de sauvegarder les modifications. Veuillez réessayer.');
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEnableAllToggle = (enabled: boolean) => {
    if (!canManageIntegrations) return;
    if (enabled && !consentGiven) {
      setPendingOption('all');
      setIsModalOpen(true);
    } else {
      const allOptions: DataSharingOptions = {
        banks: enabled, microfinance: enabled, coopec: enabled,
        analysts: enabled, partners: enabled,
      };
      save(allOptions, enabled);
    }
  };

  const handleOptionChange = (option: keyof DataSharingOptions, checked: boolean) => {
    if (!canManageIntegrations) return;
    if (checked && !consentGiven) {
      setPendingOption(option);
      setIsModalOpen(true);
    } else {
      const newOptions = { ...sharingOptions, [option]: checked };
      save(newOptions, Object.values(newOptions).every(Boolean));
    }
  };

  const handleConsentConfirmed = () => {
    if (pendingOption === 'all') {
      const allOptions: DataSharingOptions = {
        banks: true, microfinance: true, coopec: true,
        analysts: true, partners: true,
      };
      save(allOptions, true, true);
    } else if (pendingOption) {
      const newOptions = { ...sharingOptions, [pendingOption]: true };
      save(newOptions, Object.values(newOptions).every(Boolean), true);
    }
    setPendingOption(null);
    setIsModalOpen(false);
  };

  if (!canManageIntegrations && !isLoading) {
    return (
      <Card title="Partage de données" icon={Share2}>
        <p className="text-sm text-muted-foreground p-4">
          Vous n'avez pas les permissions nécessaires pour configurer le partage de données.
        </p>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card title="Partage de données" icon={Share2}>
        <div className="flex items-center justify-center h-40 gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Chargement des paramètres...</p>
        </div>
      </Card>
    );
  }

  return (
    <Card title="Partage de données" icon={Share2}>
      <div className="space-y-6">
        {/* Erreur */}
        {error && (
          <div className="flex items-start gap-3 p-3 rounded-lg border bg-destructive/5 border-destructive/20 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Info banner */}
        <div className="flex items-start gap-3 p-4 rounded-lg border bg-primary/5 border-primary/20 text-sm text-muted-foreground">
          <Info className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
          <div>
            <p>
              Le partage de vos données comptables peut vous aider à accéder plus facilement à des
              solutions de financement et à recevoir des conseils personnalisés.
            </p>
            <Button
              variant="link"
              className="p-0 h-auto text-primary hover:text-primary/80 mt-1"
              onClick={() => window.open('/data-sharing-benefits', '_blank')}
            >
              En savoir plus sur les avantages du partage de données
            </Button>
          </div>
        </div>

        {/* Toggle global */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-foreground">Activer tout le partage</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Partager les données avec tous les partenaires</p>
          </div>
          <Switch
            checked={enableAll}
            onCheckedChange={handleEnableAllToggle}
            disabled={!canManageIntegrations || isSaving}
          />
        </div>

        {/* Partage ciblé */}
        <div className="border-t border-border pt-4">
          <h3 className="text-sm font-medium text-foreground mb-3">Partage ciblé</h3>
          <div className="space-y-1">
            {(Object.keys(SHARING_LABELS) as Array<keyof DataSharingOptions>).map((key) => {
              const entry = SHARING_LABELS[key];
              if (!entry) return null;
              const { label, description } = entry;
              return (
                <div
                  key={key}
                  className="flex items-center justify-between rounded-lg px-3 py-2.5 hover:bg-muted/50 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground">{label}</p>
                    <p className="text-xs text-muted-foreground">{description}</p>
                  </div>
                  <Switch
                    checked={sharingOptions[key] ?? false}
                    onCheckedChange={(checked) => handleOptionChange(key, checked)}
                    disabled={!canManageIntegrations || isSaving}
                    className="ml-4 flex-shrink-0"
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <ConsentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConsentConfirmed}
      />
    </Card>
  );
}
